import React from "react";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { notifyFormInvalid } from "../../utils/inputRules";
import AdminIcon from "./adminIcons";
import { ADMIN_COLORS, ADMIN_CONTROL_HEIGHT, ADMIN_FONT, ADMIN_TYPE } from "./adminUi";

var ADMIN_DIALOG_WIDTH = 760;

/** Two-column field grid used inside Add/Edit dialogs; one column on phones. */
export var ADMIN_FORM_GRID_SX = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
  columnGap: "20px",
  rowGap: "18px",
};

/** "+91" start adornment for Indian mobile numbers. */
export function PhonePrefix() {
  return (
    <Box
      component="span"
      sx={Object.assign({ fontFamily: ADMIN_FONT, color: ADMIN_COLORS.labelSecondary, whiteSpace: "nowrap" }, ADMIN_TYPE.button)}
    >
      +91
    </Box>
  );
}

/** Submits through Formik and shows the standard toast when required fields are missing or invalid. */
export function submitWithValidation(formik) {
  return function (event) {
    if (event && event.preventDefault) event.preventDefault();
    formik.validateForm().then(function (errors) {
      notifyFormInvalid(errors);
    });
    formik.handleSubmit();
  };
}

/** Figma input: #FDFDFD, 1px #C7CCD1, radius 6; height and text size follow the shared admin tokens. */
export var ADMIN_INPUT_SX = {
  "& .MuiOutlinedInput-root": {
    bgcolor: ADMIN_COLORS.card,
    borderRadius: "6px",
    fontFamily: ADMIN_FONT,
    fontSize: ADMIN_TYPE.button.fontSize,
    color: ADMIN_COLORS.label,
    "& .MuiOutlinedInput-notchedOutline": { borderColor: ADMIN_COLORS.line },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: ADMIN_COLORS.labelMuted },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: ADMIN_COLORS.brand, borderWidth: "1px" },
    "&.Mui-error .MuiOutlinedInput-notchedOutline": { borderColor: ADMIN_COLORS.fieldError },
    "&.Mui-disabled": { bgcolor: ADMIN_COLORS.page },
  },
  "& .MuiOutlinedInput-input": {
    py: (ADMIN_CONTROL_HEIGHT - parseInt(ADMIN_TYPE.button.lineHeight, 10)) / 2 + "px",
    px: "10px",
    height: "auto",
    lineHeight: ADMIN_TYPE.button.lineHeight,
    fontSize: ADMIN_TYPE.button.fontSize,
    "&::placeholder": { color: ADMIN_COLORS.labelMuted, opacity: 1 },
  },
  "& .MuiInputAdornment-root": { color: ADMIN_COLORS.labelMuted },
  "& .MuiOutlinedInput-root.MuiInputBase-adornedStart": { pl: "10px" },
  "& .MuiInputBase-adornedStart .MuiOutlinedInput-input": { pl: "6px" },
  "& .MuiSelect-select": { minHeight: "auto !important" },
};

var HELPER_TEXT_SX = Object.assign({ fontFamily: ADMIN_FONT, m: 0 }, ADMIN_TYPE.helper);

export function FieldError(props) {
  if (!props.children) return null;
  return (
    <Typography role="alert" sx={Object.assign({ color: ADMIN_COLORS.fieldError }, HELPER_TEXT_SX)}>
      {props.children}
    </Typography>
  );
}

/** Label (with red * when required) above the control, error or hint below. */
export function AdminField(props) {
  return (
    <Box sx={Object.assign({ display: "flex", flexDirection: "column", gap: "6px", minWidth: 0 }, props.sx || {})}>
      {props.label ? (
        <Typography
          component="label"
          htmlFor={props.htmlFor}
          sx={Object.assign({ fontFamily: ADMIN_FONT, color: ADMIN_COLORS.label }, ADMIN_TYPE.inputLabel)}
        >
          {props.label}
          {props.required ? (
            <Box component="span" sx={{ color: ADMIN_COLORS.required, ml: "2px" }}>
              *
            </Box>
          ) : null}
        </Typography>
      ) : null}
      {props.children}
      {props.error ? (
        <FieldError>{props.error}</FieldError>
      ) : props.hint ? (
        <Typography sx={Object.assign({ color: ADMIN_COLORS.labelSecondary }, HELPER_TEXT_SX)}>{props.hint}</Typography>
      ) : null}
    </Box>
  );
}

function SelectCaret(props) {
  return (
    <AdminIcon
      name="CaretDown"
      size={16}
      className={props.className}
      sx={{ color: ADMIN_COLORS.label, right: "12px !important", top: "calc(50% - 8px) !important" }}
    />
  );
}

/**
 * TextField with the Figma label/error layout.
 * `select` + `placeholder` shows a muted placeholder while empty.
 */
export function AdminTextField(props) {
  var { label, required, error, hint, placeholder, select, SelectProps, sx, id, name, ...rest } = props;
  var fieldId = id || (name ? "admin-field-" + name : undefined);
  var selectProps = select
    ? Object.assign(
        {
          displayEmpty: true,
          IconComponent: SelectCaret,
          renderValue: function (value) {
            if (value === "" || value === null || value === undefined) {
              return <Box component="span" sx={{ color: ADMIN_COLORS.labelMuted }}>{placeholder}</Box>;
            }
            return value;
          },
          MenuProps: {
            PaperProps: {
              sx: {
                mt: "4px",
                borderRadius: "6px",
                "& .MuiMenuItem-root": Object.assign({ fontFamily: ADMIN_FONT, minHeight: 40 }, ADMIN_TYPE.body),
              },
            },
          },
        },
        SelectProps || {}
      )
    : undefined;
  return (
    <AdminField label={label} required={required} error={error} hint={hint} htmlFor={fieldId}>
      <TextField
        fullWidth
        id={fieldId}
        name={name}
        select={select}
        placeholder={select ? undefined : placeholder}
        SelectProps={selectProps}
        error={Boolean(error)}
        sx={[ADMIN_INPUT_SX].concat(sx || [])}
        {...rest}
      />
    </AdminField>
  );
}

/** Admin modal: rounded panel, title + X, divider above and below the scrollable body. */
export function AdminDialog(props) {
  var width = props.width || ADMIN_DIALOG_WIDTH;
  return (
    <Dialog
      open={props.open}
      onClose={props.busy ? undefined : props.onClose}
      maxWidth={false}
      PaperProps={{
        component: props.onSubmit ? "form" : "div",
        onSubmit: props.onSubmit,
        noValidate: props.onSubmit ? true : undefined,
        autoComplete: props.onSubmit ? "off" : undefined,
        sx: {
          width: width,
          maxWidth: "calc(100vw - 32px)",
          maxHeight: "calc(100vh - 32px)",
          m: "16px",
          borderRadius: "16px",
          p: { xs: "16px", sm: "24px" },
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          gap: "18px",
          bgcolor: ADMIN_COLORS.card,
          boxShadow: "0 12px 32px rgba(23, 26, 28, 0.16)",
        },
      }}
      aria-labelledby={props.titleId}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "20px", pb: "14px", borderBottom: "1px solid " + ADMIN_COLORS.lineSoft }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            id={props.titleId}
            sx={Object.assign({ fontFamily: ADMIN_FONT, color: ADMIN_COLORS.label }, ADMIN_TYPE.dialogTitle)}
          >
            {props.title}
          </Typography>
          {props.subtitle ? (
            <Typography sx={Object.assign({ fontFamily: ADMIN_FONT, color: ADMIN_COLORS.labelSecondary, mt: "2px" }, ADMIN_TYPE.body)}>
              {props.subtitle}
            </Typography>
          ) : null}
        </Box>
        <IconButton
          aria-label="Close"
          onClick={props.onClose}
          disabled={props.busy}
          sx={{ p: "6px", color: "#091E42" }}
        >
          <AdminIcon name="X" size={24} />
        </IconButton>
      </Box>
      <Box sx={{ overflowY: "auto", overflowX: "hidden", minHeight: 0, flex: "1 1 auto", pt: "2px", pb: "2px" }}>
        {props.children}
      </Box>
      {props.actions ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "12px",
            pt: "16px",
            borderTop: "1px solid " + ADMIN_COLORS.lineSoft,
          }}
        >
          {props.actions}
        </Box>
      ) : null}
    </Dialog>
  );
}
